import type { WorksheetModule, WorksheetSize } from "@arbeidsark/shared"
import range from "lodash/range"
import { motion } from "motion/react"
import { useState } from "react"
import styled, { css } from "styled-components"
import { Icon } from "~/ur-react-components"
import { useTranslate } from "~/ur-react-hooks"
import GenericLanguageModuleConfigurationCell from "./GenericLanguageModuleConfigurationCell"

const Layout = styled.div`
  background: white;
  box-shadow: ${(props) => props.theme.shadow.elevation1};
  width: 100%;
  border-radius: 0.5rem;
`

const Header = styled.div<{ isOpen: boolean }>`
  padding: 1.25rem 1rem; // Moved padding here to ensure height comes from content
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  border-radius: 0.5rem;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${(props) => props.theme.colors.gray95};
  }

  ${(props) =>
    props.isOpen &&
    css`
      background-color: ${props.theme.colors.primary95};
      color: ${props.theme.colors.primary20} !important;
      border-radius: 0.5rem 0.5rem 0 0;

      &:hover {
        background-color: ${props.theme.colors.primary90};
      }

      ${Title}, ${HeaderRightSide} {
        color: ${props.theme.colors.primary20};
      }
    `}
`

const Title = styled.h3`
  margin: 0;
  // padding removed from here
  color: ${(props) => props.theme.colors.gray50};
`

const HeaderRightSide = styled.aside`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  // padding removed from here
  color: ${(props) => props.theme.colors.gray60};

  font-weight: bold;
  font-size: 14px;
`

const Body = styled.div<{ isOpen: boolean; columns: number }>`
  display: ${(props) => (props.isOpen ? "grid" : "none")};

  width: 100%;
  grid-template-columns: repeat(${(props) => props.columns}, 1fr);

  /* This should hit every cell being rendered in the grid, giving us a 
  vertical line */
  ${(props) =>
    props.columns > 1 &&
    css`
      > :nth-child(2n + 1) {
        border-right: 1px solid ${(props) => props.theme.colors.gray95};
      }
    `}
`

export interface GenericLanguagePageConfigurationProps {
  pageIndex: number
  layout: WorksheetSize
  modulesConfiguration?: Record<string, WorksheetModule>
  onConfigurationChanged: (
    moduleIndex: number,
    moduleConfiguration: WorksheetModule,
  ) => void
  onRegenerateModule: (moduleIndex: number) => void
  isFirstPage?: boolean
}

const GenericLanguagePageConfiguration: React.FC<
  GenericLanguagePageConfigurationProps
> = ({
  pageIndex,
  layout,
  modulesConfiguration,
  onConfigurationChanged,
  onRegenerateModule,
  isFirstPage = false,
}) => {
  const [isOpen, setIsOpen] = useState(true)

  const translations = useTranslate({
    page: "worksheets.labels.page",
    settings: "common.settings",
    grade: "worksheets.labels.grade",
    layout: "worksheets.labels.page-layout",
  })

  const moduleNum = layout === "full" ? 1 : 2

  return (
    <Layout>
      <Header isOpen={isOpen} onClick={() => setIsOpen(!isOpen)}>
        <Title>
          {translations.page} {pageIndex + 1}
        </Title>
        <HeaderRightSide>
          {translations.settings}
          <motion.div animate={{ rotate: isOpen ? 135 : 0 }}>
            <Icon icon="plus" size="20px" type="regular" />
          </motion.div>
        </HeaderRightSide>
      </Header>
      <Body isOpen={isOpen} columns={moduleNum}>
        {range(moduleNum).map((i) => (
          <GenericLanguageModuleConfigurationCell
            key={i}
            pageIndex={pageIndex}
            moduleIndex={i}
            configuration={modulesConfiguration?.[i]}
            onConfigurationChanged={(newModuleConfiguration) => {
              onConfigurationChanged(i, newModuleConfiguration)
            }}
            onRegenerateModule={() => onRegenerateModule(i)}
            layout={layout}
            isFirstModule={isFirstPage && i === 0}
          />
        ))}
      </Body>
    </Layout>
  )
}

export default GenericLanguagePageConfiguration
